import DescriptionIcon from "@mui/icons-material/DescriptionOutlined";
import { Box, Button, Stack } from "@mui/material";
import React, { useMemo } from "react";
import DateRangeSelect from "@entrilia/shared/components/DateRangeSelect";
import RecordCounter from "@entrilia/shared/components/filters/RecordCounter";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import MultiSelectFilter from "@entrilia/shared/components/MultiSelectFilter";
import { distinct } from "@entrilia/shared/utilities/arrayHelper";
import type { DescriptiveDateRange } from "@entrilia/shared/utilities/dateFilterHelper";
import { lastYear, thisMonth, thisQuarter, thisYear } from "@entrilia/shared/utilities/dateFilterHelper";
import type { DataCollectionSubmissionInfo } from "../../api/types/dataCollectionTypes";
import type { DataSubmissionsFilterState } from "./dataSubmissionsFilterState";

interface Props {
  allRows: DataCollectionSubmissionInfo[];
  filteredRowsCount: number;
  isLoading: boolean;
  filterState: DataSubmissionsFilterState;
  setFilterState: React.Dispatch<React.SetStateAction<DataSubmissionsFilterState>>;
}

const dateRangeOptions = [thisMonth, thisQuarter, thisYear, lastYear];

const DataSubmissionsFilterPanel = ({ allRows, filteredRowsCount, isLoading, filterState, setFilterState }: Props) => {
  const requestTemplateNames = useMemo(() => distinct(allRows.map((ds) => ds.requestTemplateName)).sort(), [allRows]);

  const handleSearch = (searchValue: string) => {
    setFilterState((prev) => ({ ...prev, searchValue }));
  };

  const handleTemplateNameChange = (selectedTemplateNames: string[]) => {
    setFilterState((prev) => ({ ...prev, selectedTemplateNames }));
  };

  const handleReportingDateRangeChange = (reportingDateRange: DescriptiveDateRange) => {
    setFilterState((prev) => ({ ...prev, reportingDateRange }));
  };

  const handleClearFilters = () => {
    setFilterState((prev) => ({ ...prev, selectedTemplateNames: [], reportingDateRange: undefined }));
  };

  const showClearFilters =
    !isLoading && (filterState.selectedTemplateNames.length > 0 || filterState.reportingDateRange);

  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", pb: 2.5 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <MultiSelectFilter
          options={requestTemplateNames}
          selectedValues={filterState.selectedTemplateNames}
          onChange={handleTemplateNameChange}
          getOptionValue={(name) => name}
          getOptionLabel={(name) => name}
          disabled={isLoading || requestTemplateNames.length === 0}
          placeholder="Type"
          StartIcon={DescriptionIcon}
        />
        <DateRangeSelect
          predefinedRanges={dateRangeOptions}
          dateRange={filterState.reportingDateRange}
          onChange={handleReportingDateRangeChange}
          placeholder="Reporting Date"
          disabled={isLoading}
        />
        {showClearFilters && (
          <Button variant="text" onClick={handleClearFilters}>
            Clear all
          </Button>
        )}
        <Box sx={{ pl: 1 }}>
          <RecordCounter records={filteredRowsCount} totalRecords={allRows.length} hide={isLoading} />
        </Box>
      </Stack>
      <SearchField
        debounceTimeMs={300}
        initialValue={filterState.searchValue}
        onSearch={handleSearch}
        disabled={isLoading}
      />
    </Box>
  );
};

export default DataSubmissionsFilterPanel;
